export type GridValue = "1/4" | "1/8" | "1/16" | "1/32";
export type AccompanimentMode = "off" | "chords" | "arpeggio";
export type HarmonyRoute = "melody-fit" | "6451" | "smooth" | "dramatic";
export type TrackRole = "melody" | "bass" | "strings" | "synth";

export type PitchFrame = {
  time: number;
  frequency: number | null;
  midiFloat: number | null;
  confidence: number;
  level?: number;
  acceptanceConfidence?: number;
  cents?: number | null;
  voiced?: boolean;
};

export type MidiNoteSource = "AUTO" | "TAP_REFINE" | "RHYTHM_FIRST" | "VOICE" | "MANUAL";
export type MidiNoteStatus = "RESOLVED" | "UNRESOLVED";
export type MidiNoteRefinement = "LOCAL_PATTERN";

export type MidiNote = {
  midi: number;
  startBeat: number;
  durationBeats: number;
  velocity: number;
  pitch?: number | null;
  status?: MidiNoteStatus;
  source?: MidiNoteSource;
  rhythmSlotId?: string;
  rawStartTick?: number;
  rawEndTick?: number;
  deviceCorrectedStartTick?: number;
  deviceCorrectedEndTick?: number;
  quantizedStartTick?: number;
  quantizedEndTick?: number;
  refinedStartTick?: number;
  refinedEndTick?: number;
  refinement?: MidiNoteRefinement;
  refinementConfidence?: number;
};

export function isUnresolvedMidiNote(note: MidiNote): boolean {
  return note.status === "UNRESOLVED" || note.pitch === null;
}

export function resolvedMidiNotes(notes: MidiNote[]): MidiNote[] {
  return notes.filter((note) => !isUnresolvedMidiNote(note));
}

export type MidiTrack = {
  name: string;
  role: TrackRole;
  notes: MidiNote[];
};

export type KeyCandidate = {
  tonic: string;
  tonicIndex: number;
  mode: "major" | "minor";
  score: number;
  confidence: number;
};

export type SensitivityProfile = {
  level: number;
  rmsThreshold: number;
  confidenceThreshold: number;
  voicedThreshold: number;
};

export type ChordCandidate = {
  degree: number;
  label: string;
  reason: string;
  warning: boolean;
};

export type ChordSuggestion = {
  bar: number;
  degree: number;
  label: string;
  reason: string;
  candidates: ChordCandidate[];
  warning: boolean;
};
export type BarProcessingSetting = {
  grid: GridValue;
  minimumBeats: number | null;
  detail?: number;
};

export type NoteDetailProfile = {
  level: number;
  label: "SMOOTH" | "NORMAL" | "FINE" | "ULTRA";
  voicedRatioMultiplier: number;
  reattackGapSeconds: number;
  reattackDipRatio: number;
  preserveSingleCell: boolean;
};

export type ChordTransition = {
  atBeat: number;
  label: string;
  notes: number[];
};


export const NOTE_NAMES = ["C", "C♯", "D", "D♯", "E", "F", "F♯", "G", "G♯", "A", "A♯", "B"];
export const GRID_BEATS: Record<GridValue, number> = { "1/4": 1, "1/8": 0.5, "1/16": 0.25, "1/32": 0.125 };
export const MIN_TRACKED_FREQUENCY = 55;
export const MAX_TRACKED_FREQUENCY = 2200;
export const MIN_MANUAL_NOTE_BEATS = 0.125;

const MAJOR_PROFILE = [6.35, 2.23, 3.48, 2.33, 4.38, 4.09, 2.52, 5.19, 2.39, 3.66, 2.29, 2.88];
const MINOR_PROFILE = [6.33, 2.68, 3.52, 5.38, 2.6, 3.53, 2.54, 4.75, 3.98, 2.69, 3.34, 3.17];

export function frequencyToMidi(frequency: number): number {
  return 69 + 12 * Math.log2(frequency / 440);
}

export function midiToFrequency(midi: number): number {
  return 440 * 2 ** ((midi - 69) / 12);
}

export function midiToNoteName(midi: number): string {
  const rounded = Math.round(midi);
  return `${NOTE_NAMES[((rounded % 12) + 12) % 12]}${Math.floor(rounded / 12) - 1}`;
}

function median(values: number[]): number {
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
}

export function sensitivityProfile(value: number): SensitivityProfile {
  const level = Math.max(0, Math.min(100, Math.round(value)));
  return {
    level,
    rmsThreshold: 0.026 - level * 0.00022,
    confidenceThreshold: 0.72 - level * 0.0024,
    voicedThreshold: 0.55 - level * 0.003,
  };
}
export function midiNoteKey(note: Pick<MidiNote, "midi" | "startBeat">): string {
  return `${note.startBeat.toFixed(4)}:${note.midi}`;
}

export function applyNoteDurationOverrides(notes: MidiNote[], overrides: Record<string, number>): MidiNote[] {
  return notes.map((note, index) => {
    const requested = overrides[midiNoteKey(note)];
    if (requested === undefined) return { ...note };
    const nextStart = notes.slice(index + 1).map((next) => next.startBeat).find((start) => start > note.startBeat);
    const maximum = nextStart === undefined ? 64 : Math.max(MIN_MANUAL_NOTE_BEATS, nextStart - note.startBeat);
    return {
      ...note,
      durationBeats: Math.max(MIN_MANUAL_NOTE_BEATS, Math.min(requested, maximum)),
    };
  });
}

export function noteDetailProfile(value: number): NoteDetailProfile {
  const level = Math.max(0, Math.min(100, Math.round(value)));
  return {
    level,
    label: level >= 85 ? "ULTRA" : level >= 65 ? "FINE" : level >= 35 ? "NORMAL" : "SMOOTH",
    voicedRatioMultiplier: 1.1 - level * 0.004,
    reattackGapSeconds: 0.16 - level * 0.001,
    reattackDipRatio: 0.25 + level * 0.004,
    preserveSingleCell: level >= 65,
  };
}


export function ensureMinimumNoteDuration(notes: MidiNote[], minimumBeats: number): MidiNote[] {
  if (minimumBeats <= 0) return notes.map((note) => ({ ...note }));
  return notes.map((note, index) => {
    if (note.durationBeats >= minimumBeats) return { ...note };
    const nextStart = notes
      .slice(index + 1)
      .map((next) => next.startBeat)
      .find((startBeat) => startBeat > note.startBeat);
    const desiredEnd = note.startBeat + minimumBeats;
    const cappedEnd = nextStart === undefined ? desiredEnd : Math.min(desiredEnd, nextStart);
    return {
      ...note,
      durationBeats: Math.max(note.durationBeats, cappedEnd - note.startBeat),
    };
  });
}

export function quantizePitchFrames(
  frames: PitchFrame[],
  bpm: number,
  grid: GridValue,
  voicedThreshold = 0.4,
  confidenceThreshold = 0.55,
  detailLevel = 50,
): MidiNote[] {
  if (!frames.length) return [];
  const gridBeats = GRID_BEATS[grid];
  const gridSeconds = (60 / bpm) * gridBeats;
  const maxTime = Math.max(...frames.map((frame) => frame.time));
  const cellCount = Math.max(1, Math.ceil((maxTime + 0.001) / gridSeconds));
  const detail = noteDetailProfile(detailLevel);
  const effectiveVoicedThreshold = Math.max(0.15, Math.min(0.95, voicedThreshold * detail.voicedRatioMultiplier));
  const cells: Array<number | null> = [];
  const voicedCells: PitchFrame[][] = [];
  const reattacks = new Set<number>();

  for (let index = 0; index < cellCount; index += 1) {
    const start = index * gridSeconds;
    const inCell = frames.filter((frame) => frame.time >= start && frame.time < start + gridSeconds);
    const voiced = inCell.filter(
      (frame) => frame.frequency !== null && frame.frequency >= MIN_TRACKED_FREQUENCY && frame.frequency <= MAX_TRACKED_FREQUENCY && frame.confidence >= (frame.acceptanceConfidence ?? confidenceThreshold),
    );
    if (!inCell.length || voiced.length / inCell.length < effectiveVoicedThreshold) {
      cells.push(null);
      voicedCells.push([]);
      continue;
    }
    cells.push(Math.round(frequencyToMidi(median(voiced.map((frame) => frame.frequency as number)))));
    voicedCells.push(voiced);
  }

  if (!detail.preserveSingleCell) {
    for (let index = 1; index < cells.length - 1; index += 1) {
      if (cells[index - 1] !== null && cells[index - 1] === cells[index + 1] && cells[index] !== cells[index - 1]) {
        cells[index] = cells[index - 1];
      }
    }
  }

  for (let index = 1; index < cells.length; index += 1) {
    if (cells[index] === null || cells[index] !== cells[index - 1]) continue;
    const previousVoiced = voicedCells[index - 1];
    const currentVoiced = voicedCells[index];
    if (!previousVoiced.length || !currentVoiced.length) continue;
    const lastPrevious = previousVoiced.at(-1) as PitchFrame;
    const firstCurrent = currentVoiced[0];
    const gapSeconds = firstCurrent.time - lastPrevious.time;
    const hasUnvoicedGap = frames.some((frame) => frame.time > lastPrevious.time && frame.time < firstCurrent.time && (frame.frequency === null || frame.confidence < (frame.acceptanceConfidence ?? confidenceThreshold)));
    const boundary = index * gridSeconds;
    const levelWindow = Math.min(0.25, gridSeconds);
    const leftLevels = frames.filter((frame) => frame.time >= boundary - levelWindow && frame.time < boundary && typeof frame.level === "number").map((frame) => frame.level as number);
    const rightLevels = frames.filter((frame) => frame.time >= boundary && frame.time <= boundary + levelWindow && typeof frame.level === "number").map((frame) => frame.level as number);
    const valleyLevels = frames.filter((frame) => Math.abs(frame.time - boundary) <= Math.min(0.09, gridSeconds * 0.5) && typeof frame.level === "number").map((frame) => frame.level as number);
    const edgePeak = leftLevels.length && rightLevels.length ? Math.min(Math.max(...leftLevels), Math.max(...rightLevels)) : 0;
    const valley = valleyLevels.length ? Math.min(...valleyLevels) : Number.POSITIVE_INFINITY;
    const levelDip = edgePeak > 0 && valley / edgePeak <= detail.reattackDipRatio;
    if ((hasUnvoicedGap && gapSeconds >= detail.reattackGapSeconds) || levelDip) reattacks.add(index);
  }

  const notes: MidiNote[] = [];
  cells.forEach((midi, index) => {
    if (midi === null) return;
    const previous = notes.at(-1);
    const startBeat = index * gridBeats;
    if (previous && previous.midi === midi && !reattacks.has(index) && Math.abs(previous.startBeat + previous.durationBeats - startBeat) < 0.0001) {
      previous.durationBeats += gridBeats;
    } else {
      notes.push({ midi, startBeat, durationBeats: gridBeats, velocity: reattacks.has(index) ? 100 : 92 });
    }
  });
  return notes;
}
export function quantizePitchFramesByBar(
  frames: PitchFrame[],
  bpm: number,
  defaultGrid: GridValue,
  barSettings: Record<number, BarProcessingSetting>,
  voicedThreshold = 0.4,
  confidenceThreshold = 0.55,
  defaultDetail = 50,
): MidiNote[] {
  if (!Object.keys(barSettings).length) return quantizePitchFrames(frames, bpm, defaultGrid, voicedThreshold, confidenceThreshold, defaultDetail);
  const secondsPerBeat = 60 / bpm;
  const barSeconds = secondsPerBeat * 4;
  const maxTime = frames.length ? Math.max(...frames.map((frame) => frame.time)) : 0;
  const barCount = Math.max(1, Math.ceil((maxTime + 0.001) / barSeconds));
  return Array.from({ length: barCount }, (_, bar) => {
    const startSeconds = bar * barSeconds;
    const localFrames = frames
      .filter((frame) => frame.time >= startSeconds && frame.time < startSeconds + barSeconds)
      .map((frame) => ({ ...frame, time: frame.time - startSeconds }));
    const setting = barSettings[bar];
    return quantizePitchFrames(localFrames, bpm, setting?.grid ?? defaultGrid, voicedThreshold, confidenceThreshold, setting?.detail ?? defaultDetail)
      .map((note) => ({ ...note, startBeat: note.startBeat + bar * 4 }));
  }).flat();
}

export function ensureMinimumNoteDurationByBar(
  notes: MidiNote[],
  defaultMinimumBeats: number | null,
  barSettings: Record<number, BarProcessingSetting>,
): MidiNote[] {
  return notes.map((note, index) => {
    const bar = Math.floor(note.startBeat / 4);
    const minimumBeats = barSettings[bar] ? barSettings[bar].minimumBeats : defaultMinimumBeats;
    if (minimumBeats === null || minimumBeats <= 0 || note.durationBeats >= minimumBeats) return { ...note };
    const nextStart = notes.slice(index + 1).map((next) => next.startBeat).find((start) => start > note.startBeat);
    const barEnd = (bar + 1) * 4;
    const desiredEnd = note.startBeat + minimumBeats;
    const cappedEnd = Math.min(desiredEnd, nextStart ?? desiredEnd, barEnd);
    return { ...note, durationBeats: Math.max(note.durationBeats, cappedEnd - note.startBeat) };
  });
}


function correlation(histogram: number[], profile: number[], tonic: number): number {
  const rotated = profile.map((_, index) => profile[(index - tonic + 12) % 12]);
  const meanA = histogram.reduce((sum, value) => sum + value, 0) / 12;
  const meanB = rotated.reduce((sum, value) => sum + value, 0) / 12;
  let numerator = 0;
  let denominatorA = 0;
  let denominatorB = 0;
  for (let index = 0; index < 12; index += 1) {
    const a = histogram[index] - meanA;
    const b = rotated[index] - meanB;
    numerator += a * b;
    denominatorA += a * a;
    denominatorB += b * b;
  }
  return denominatorA && denominatorB ? numerator / Math.sqrt(denominatorA * denominatorB) : 0;
}

export function detectKeys(notes: MidiNote[]): KeyCandidate[] {
  if (!notes.length) return [];
  const histogram = Array.from({ length: 12 }, () => 0);
  notes.forEach((note) => { histogram[((note.midi % 12) + 12) % 12] += note.durationBeats; });
  const raw: KeyCandidate[] = [];
  for (let tonic = 0; tonic < 12; tonic += 1) {
    for (const mode of ["major", "minor"] as const) {
      raw.push({
        tonic: NOTE_NAMES[tonic],
        tonicIndex: tonic,
        mode,
        score: correlation(histogram, mode === "major" ? MAJOR_PROFILE : MINOR_PROFILE, tonic),
        confidence: 0,
      });
    }
  }
  raw.sort((a, b) => b.score - a.score);
  const minScore = Math.min(...raw.map((item) => item.score));
  const weights = raw.map((item) => Math.exp((item.score - minScore) * 2.4));
  const maxWeight = Math.max(...weights);
  return raw.slice(0, 3).map((item, index) => ({ ...item, confidence: Math.round((weights[index] / maxWeight) * 100) }));
}

function variableLength(value: number): number[] {
  let buffer = value & 0x7f;
  const bytes: number[] = [];
  while ((value >>= 7)) {
    buffer <<= 8;
    buffer |= (value & 0x7f) | 0x80;
  }
  while (true) {
    bytes.push(buffer & 0xff);
    if (buffer & 0x80) buffer >>= 8;
    else break;
  }
  return bytes;
}

const uint16 = (value: number) => [(value >> 8) & 0xff, value & 0xff];
const uint32 = (value: number) => [(value >> 24) & 0xff, (value >> 16) & 0xff, (value >> 8) & 0xff, value & 0xff];
const programForRole: Record<TrackRole, number> = { melody: 0, bass: 33, strings: 48, synth: 80 };

function channelForTrack(index: number): number {
  const channel = index % 15;
  return channel >= 9 ? channel + 1 : channel;
}

function buildTrackChunk(events: Array<{ tick: number; priority: number; data: number[] }>): number[] {
  events.sort((a, b) => a.tick - b.tick || a.priority - b.priority);
  const track: number[] = [];
  let previousTick = 0;
  events.forEach((event) => {
    track.push(...variableLength(event.tick - previousTick), ...event.data);
    previousTick = event.tick;
  });
  track.push(0x00, 0xff, 0x2f, 0x00);
  return [0x4d, 0x54, 0x72, 0x6b, ...uint32(track.length), ...track];
}

function trackNameEvent(name: string): number[] {
  const bytes = [...new TextEncoder().encode(name.slice(0, 48))];
  return [0xff, 0x03, ...variableLength(bytes.length), ...bytes];
}

export type MidiTimeSignature = {
  numerator: number;
  denominator: number;
};

export type MidiTimingMetadata = {
  ppq?: number;
  timeSignature?: MidiTimeSignature;
};

function earliestNoteTick(notes: MidiNote[], ppq: number): number {
  return notes.length ? Math.min(...notes.map((note) => Math.round(note.startBeat * ppq))) : 0;
}

export function midiExportOffsetTicks(notes: MidiNote[], ppq: number): number {
  return Math.max(0, -earliestNoteTick(notes, ppq));
}

function noteEvents(notes: MidiNote[], channel: number, ppq: number, offsetTicks = 0): Array<{ tick: number; priority: number; data: number[] }> {
  return notes.flatMap((note) => {
    const start = Math.max(0, Math.round(note.startBeat * ppq) + offsetTicks);
    const end = Math.max(start + 1, Math.round((note.startBeat + note.durationBeats) * ppq) + offsetTicks);
    return [
      { tick: start, priority: 2, data: [0x90 | channel, note.midi, note.velocity] },
      { tick: end, priority: 0, data: [0x80 | channel, note.midi, 0x00] },
    ];
  });
}

function timeSignatureEvent(timeSignature: MidiTimeSignature): number[] {
  const numerator = Math.max(1, Math.min(32, Math.round(timeSignature.numerator)));
  const denominator = Math.max(1, Math.min(32, Math.round(timeSignature.denominator)));
  const exponent = Math.max(0, Math.min(5, Math.round(Math.log2(denominator))));
  return [0xff, 0x58, 0x04, numerator, exponent, 24, 8];
}

function safePpq(timing?: MidiTimingMetadata): number {
  return Math.max(24, Math.min(0x7fff, Math.round(timing?.ppq ?? 480)));
}

export function buildMidiFile(
  notes: MidiNote[],
  bpm: number,
  name = "Humming MIDI",
  role: TrackRole = "melody",
  timing?: MidiTimingMetadata,
): Uint8Array {
  const ppq = safePpq(timing);
  const microseconds = Math.round(60_000_000 / bpm);
  const events: Array<{ tick: number; priority: number; data: number[] }> = [
    { tick: 0, priority: 0, data: [0xff, 0x51, 0x03, (microseconds >> 16) & 0xff, (microseconds >> 8) & 0xff, microseconds & 0xff] },
    { tick: 0, priority: 0, data: trackNameEvent(name) },
    { tick: 0, priority: 0, data: timeSignatureEvent(timing?.timeSignature ?? { numerator: 4, denominator: 4 }) },
    { tick: 0, priority: 1, data: [0xc0, programForRole[role]] },
    ...noteEvents(notes, 0, ppq, midiExportOffsetTicks(notes, ppq)),
  ];
  const track = buildTrackChunk(events);
  return new Uint8Array([
    0x4d, 0x54, 0x68, 0x64, ...uint32(6), ...uint16(0), ...uint16(1), ...uint16(ppq),
    ...track,
  ]);
}

export function buildMultiTrackMidiFile(tracks: MidiTrack[], bpm: number, timing?: MidiTimingMetadata): Uint8Array {
  const ppq = safePpq(timing);
  const microseconds = Math.round(60_000_000 / bpm);
  const tempoTrack = buildTrackChunk([
    { tick: 0, priority: 0, data: trackNameEvent("Tempo") },
    { tick: 0, priority: 1, data: [0xff, 0x51, 0x03, (microseconds >> 16) & 0xff, (microseconds >> 8) & 0xff, microseconds & 0xff] },
    { tick: 0, priority: 1, data: timeSignatureEvent(timing?.timeSignature ?? { numerator: 4, denominator: 4 }) },
  ]);
  const exportOffsetTicks = midiExportOffsetTicks(tracks.flatMap((track) => track.notes), ppq);
  const midiTracks = tracks.map((track, index) => {
    const channel = channelForTrack(index);
    return buildTrackChunk([
      { tick: 0, priority: 0, data: trackNameEvent(track.name) },
      { tick: 0, priority: 1, data: [0xc0 | channel, programForRole[track.role]] },
      ...noteEvents(track.notes, channel, ppq, exportOffsetTicks),
    ]);
  });
  return new Uint8Array([
    0x4d, 0x54, 0x68, 0x64, ...uint32(6), ...uint16(1), ...uint16(tracks.length + 1), ...uint16(ppq),
    ...tempoTrack,
    ...midiTracks.flat(),
  ]);
}
export function scalePitchClasses(tonic: number, mode: "major" | "minor"): Set<number> {
  const intervals = mode === "major" ? [0, 2, 4, 5, 7, 9, 11] : [0, 2, 3, 5, 7, 8, 10];
  return new Set(intervals.map((interval) => (tonic + interval) % 12));
}

export function calculateTapTempo(timestamps: number[]): number | null {
  if (timestamps.length < 2) return null;
  const recent = timestamps.slice(-9);
  const intervals = recent.slice(1).map((time, index) => time - recent[index]).filter((interval) => interval >= 250 && interval <= 1500);
  if (!intervals.length) return null;
  return Math.max(40, Math.min(240, Math.round(60_000 / median(intervals))));
}

const SCALE_INTERVALS = {
  major: [0, 2, 4, 5, 7, 9, 11],
  minor: [0, 2, 3, 5, 7, 8, 10],
} as const;

function midiAtOrAbove(pitchClass: number, floor: number): number {
  const normalized = ((pitchClass % 12) + 12) % 12;
  return floor + ((normalized - (floor % 12) + 12) % 12);
}

export function buildChord(
  tonicIndex: number,
  mode: "major" | "minor",
  degree: number,
  seventh = false,
  floor = 48,
): number[] {
  const scale = SCALE_INTERVALS[mode];
  const degreeIndex = ((degree % 7) + 7) % 7;
  const rootPitchClass = (tonicIndex + scale[degreeIndex]) % 12;
  const root = midiAtOrAbove(rootPitchClass, floor);
  const chordDegrees = seventh ? [0, 2, 4, 6] : [0, 2, 4];
  return chordDegrees.map((step) => {
    const absoluteIndex = degreeIndex + step;
    const octave = Math.floor(absoluteIndex / 7) * 12;
    return root + ((scale[absoluteIndex % 7] - scale[degreeIndex] + 12) % 12) + octave;
  });
}

const ROMAN_LABELS = {
  major: ["I", "ii", "iii", "IV", "V", "vi", "vii°"],
  minor: ["i", "ii°", "III", "iv", "v", "VI", "VII"],
} as const;

function melodyPitchClassInBar(melody: MidiNote[], barStart: number): number | null {
  const weights = Array.from({ length: 12 }, () => 0);
  melody.forEach((note) => {
    const overlap = Math.max(0, Math.min(note.startBeat + note.durationBeats, barStart + 4) - Math.max(note.startBeat, barStart));
    if (overlap > 0) weights[((note.midi % 12) + 12) % 12] += overlap;
  });
  const maximum = Math.max(...weights);
  return maximum > 0 ? weights.indexOf(maximum) : null;
}

function chordPitchClasses(chord: number[]): number[] {
  return chord.map((midi) => ((midi % 12) + 12) % 12);
}

function hasSemitoneRisk(melodyPitchClass: number | null, pitchClasses: number[]): boolean {
  if (melodyPitchClass === null || pitchClasses.includes(melodyPitchClass)) return false;
  return pitchClasses.some((pitchClass) => {
    const interval = (pitchClass - melodyPitchClass + 12) % 12;
    return interval === 1 || interval === 11;
  });
}

function chordReason(melodyPitchClass: number | null, pitchClasses: number[], route: HarmonyRoute): string {
  if (route === "6451") return "定番6→4→5→1を優先";
  if (route === "dramatic") return "5→1の解決感を優先";
  if (route === "smooth") return "前コードの共通音を残す";
  const position = melodyPitchClass === null ? -1 : pitchClasses.indexOf(melodyPitchClass);
  if (position === 0) return "メロディーを第1音にして安定";
  if (position === 1) return "メロディーを第3音にして感情的";
  if (position === 2) return "メロディーを第5音にして直線的";
  return "メロディーをテンションとして残す";
}

export function suggestChordProgression(
  melody: MidiNote[],
  key: KeyCandidate | null,
  route: HarmonyRoute = "melody-fit",
): ChordSuggestion[] {
  if (!melody.length || !key) return [];
  const endBeat = Math.max(...melody.map((note) => note.startBeat + note.durationBeats));
  const suggestions: ChordSuggestion[] = [];
  let previousDegree: number | null = null;
  for (let barStart = 0, bar = 0; barStart < endBeat; barStart += 4, bar += 1) {
    const melodyPitchClass = melodyPitchClassInBar(melody, barStart);
    const previousPitchClasses = previousDegree === null ? [] : chordPitchClasses(buildChord(key.tonicIndex, key.mode, previousDegree));
    const scored = Array.from({ length: 7 }, (_, degree) => {
      const pitchClasses = chordPitchClasses(buildChord(key.tonicIndex, key.mode, degree));
      const containsMelody = melodyPitchClass !== null && pitchClasses.includes(melodyPitchClass);
      const commonTones = pitchClasses.filter((pitchClass) => previousPitchClasses.includes(pitchClass)).length;
      const repeated = degree === previousDegree;
      const risk = hasSemitoneRisk(melodyPitchClass, pitchClasses);
      const score = (containsMelody ? 20 : 0) + (route === "smooth" ? commonTones * 4 : commonTones) - (repeated ? 2 : 0) - (risk ? 8 : 0);
      return { degree, score, pitchClasses };
    }).sort((a, b) => b.score - a.score || a.degree - b.degree);
    const fixedDegree = route === "6451" ? [5, 3, 4, 0][bar % 4] : route === "dramatic" ? [4, 0][bar % 2] : null;
    const candidates: ChordCandidate[] = scored.map((item) => ({
      degree: item.degree,
      label: ROMAN_LABELS[key.mode][item.degree],
      reason: chordReason(melodyPitchClass, item.pitchClasses, route),
      warning: hasSemitoneRisk(melodyPitchClass, item.pitchClasses),
    }));
    if (fixedDegree !== null) {
      const fixedIndex = candidates.findIndex((candidate) => candidate.degree === fixedDegree);
      const [fixed] = candidates.splice(fixedIndex, 1);
      candidates.unshift(fixed);
    }
    const selected = candidates[0];
    suggestions.push({
      bar,
      degree: selected.degree,
      label: selected.label,
      reason: selected.reason,
      candidates,
      warning: selected.warning,
    });
    previousDegree = selected.degree;
  }
  return suggestions;
}
export function buildSmoothTransitions(
  plan: Array<Pick<ChordSuggestion, "bar" | "degree" | "label">>,
  key: KeyCandidate | null,
): ChordTransition[] {
  if (!key || plan.length < 2) return [];
  return plan.slice(1).map((next, index) => {
    const previous = plan[index];
    if (previous.degree === next.degree) return null;
    const nextRoot = buildChord(key.tonicIndex, key.mode, next.degree, false, 36)[0];
    return {
      atBeat: next.bar * 4 - 0.5,
      label: `${previous.label}/${NOTE_NAMES[nextRoot % 12]}`,
      notes: [...buildChord(key.tonicIndex, key.mode, previous.degree, false, 48), nextRoot],
    };
  }).filter((item): item is ChordTransition => item !== null && item.atBeat >= 0);
}


export function generateAccompaniment(
  melody: MidiNote[],
  key: KeyCandidate | null,
  mode: AccompanimentMode,
  seventh = false,
  progressionDegrees?: number[],
  transitions: ChordTransition[] = [],
): MidiNote[] {
  if (!melody.length || !key || mode === "off") return [];
  const endBeat = Math.max(...melody.map((note) => note.startBeat + note.durationBeats));
  const progression = key.mode === "major" ? [0, 4, 5, 3] : [0, 5, 2, 6];
  const result: MidiNote[] = [];
  for (let barStart = 0, bar = 0; barStart < endBeat; barStart += 4, bar += 1) {
    const degree = progressionDegrees?.[bar] ?? progression[bar % progression.length];
    const chord = buildChord(key.tonicIndex, key.mode, degree, seventh, 43);
    if (mode === "chords") {
      chord.forEach((midi) => result.push({ midi, startBeat: barStart, durationBeats: Math.min(3.45, endBeat - barStart), velocity: 58 }));
    } else {
      for (let step = 0; step < 8 && barStart + step * 0.5 < endBeat; step += 1) {
        result.push({ midi: chord[step % chord.length], startBeat: barStart + step * 0.5, durationBeats: 0.44, velocity: 62 });
      }
    }
  }
  transitions.filter((transition) => transition.atBeat < endBeat).forEach((transition) => {
    if (mode === "chords") {
      transition.notes.forEach((midi) => result.push({ midi, startBeat: transition.atBeat, durationBeats: 0.45, velocity: 52 }));
    } else {
      transition.notes.slice(0, 3).forEach((midi, index) => result.push({ midi, startBeat: transition.atBeat + index * 0.14, durationBeats: 0.2, velocity: 55 }));
    }
  });
  return result;
}
